import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Sparkles, CheckCircle2, Loader2, Circle, Download, X,
  FileText, FileType, AlertCircle, Ban, Share2, Play
} from "lucide-react";
import { useLanguage } from "@/i18n";
import { requestPermissionIfNeeded, notifyIfHidden } from "@/utils/notifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live Deep Research progress + report viewer.
 * Uses SSE stream to update phase progress; polls job when complete.
 */
export const DeepResearchJobView = ({ open, onOpenChange, jobId, authToken }) => {
  const { t } = useLanguage();
  const [job, setJob] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const eventSrcRef = useRef(null);

  // SSE subscription
  useEffect(() => {
    if (!open || !jobId) return;

    // Ask for notification permission when user opens a job (background alert)
    requestPermissionIfNeeded();

    // Load initial job data
    (async () => {
      try {
        const res = await axios.get(`${API}/deep-research/jobs/${jobId}`);
        setJob(res.data);
        setProgressData({
          status: res.data.status,
          progress_pct: res.data.progress_pct,
          total_agents: res.data.total_agents,
          completed_agents: res.data.completed_agents,
          phases: res.data.phases,
        });
      } catch (e) {
        console.error(e);
      }
    })();

    // Use fetch-based SSE parser because EventSource cannot send auth headers
    const controller = new AbortController();
    eventSrcRef.current = controller;

    (async () => {
      try {
        const res = await fetch(`${API}/deep-research/jobs/${jobId}/stream`, {
          headers: { Authorization: `Bearer ${authToken}` },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const chunk of parts) {
            const line = chunk.trim();
            if (line.startsWith("data:")) {
              const payload = line.slice(5).trim();
              try {
                const data = JSON.parse(payload);
                setProgressData(data);
                if (data.status === "completed") {
                  // Fetch final report
                  const rj = await axios.get(`${API}/deep-research/jobs/${jobId}`);
                  setJob(rj.data);
                  // Fire background browser notification if tab is hidden
                  notifyIfHidden({
                    title: "🎯 KaanLab raporunuz hazır!",
                    body: `${data.total_agents || 0} ajan çalıştı — raporu indirebilirsiniz.`,
                    tag: `dr-job-${jobId}`,
                    onClick: () => window.focus(),
                  });
                }
                if (data.status === "failed") {
                  notifyIfHidden({
                    title: "⚠️ KaanLab görevi başarısız",
                    body: "Detaylar için uygulamayı açın.",
                    tag: `dr-job-${jobId}`,
                  });
                }
              } catch (e) { /* ignore */ }
            }
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") console.error("SSE error", e);
      }
    })();

    // Polling fallback: guarantees progress/completion updates even if the SSE
    // stream is dropped by a proxy/ingress (common on production for long-lived
    // connections). Polls every 4s until the job reaches a terminal state.
    let stopped = false;
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/deep-research/jobs/${jobId}`);
        const d = res.data;
        setProgressData((prev) => ({
          status: d.status,
          progress_pct: d.progress_pct,
          total_agents: d.total_agents,
          completed_agents: d.completed_agents,
          phases: d.phases,
          planner_rationale: d.planner_rationale ?? prev?.planner_rationale,
          complexity: d.complexity ?? prev?.complexity,
        }));
        if (["completed", "failed", "cancelled"].includes(d.status)) {
          setJob(d);
          if (!stopped) { stopped = true; clearInterval(poll); }
        }
      } catch (e) { /* transient network error — keep polling */ }
    }, 4000);

    return () => {
      controller.abort();
      clearInterval(poll);
    };
  }, [open, jobId, authToken, reloadKey]);

  const cancelJob = async () => {
    if (!confirm(t("dr_job_cancel_confirm"))) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/deep-research/jobs/${jobId}/cancel`);
      toast.success(t("dr_job_cancelled"));
      setProgressData((p) => ({ ...(p || {}), status: "cancelled" }));
      eventSrcRef.current?.abort?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dr_error"));
    } finally {
      setCancelling(false);
    }
  };

  const resumeJob = async () => {
    setResuming(true);
    try {
      await axios.post(`${API}/deep-research/jobs/${jobId}/resume`);
      toast.success(t("dr_job_resumed"));
      setProgressData((p) => ({ ...(p || {}), status: "queued" }));
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dr_error"));
    } finally {
      setResuming(false);
    }
  };

  const shareReport = async () => {    setSharing(true);
    try {
      const text = job?.report_md || "";
      const title = `KaanLab · ${(job?.task || "").slice(0, 60)}`;
      if (navigator.share) {
        await navigator.share({ title, text: text.slice(0, 6000) });
        toast.success(t("dr_shared_ok"));
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(t("dr_shared_copied"));
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error(t("dr_share_failed"));
    } finally {
      setSharing(false);
    }
  };

  const downloadReport = async (format) => {    setDownloading(format);
    try {
      const res = await axios.get(`${API}/deep-research/jobs/${jobId}/report.${format}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `deep_research_${jobId.slice(0, 8)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("dr_download_failed"));
    } finally {
      setDownloading(null);
    }
  };

  const status = progressData?.status || job?.status || "queued";
  const pct = progressData?.progress_pct || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="dr-job-view"
        className="max-w-3xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-950/30 border-purple-500/30 p-0"
      >
        <button
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-zinc-800/60 hover:bg-zinc-700 text-zinc-300"
        >
          <X size={16} />
        </button>

        <div className="p-6 pb-4 border-b border-zinc-800">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white">{t("dr_job_title")}</h2>
              <p className="text-xs text-zinc-500 line-clamp-1">{job?.task || t("loading")}</p>
            </div>
            <StatusBadge status={status} t={t} />
            {(status === "running" || status === "queued") && (
              <Button
                data-testid="dr-job-cancel-btn"
                onClick={cancelJob}
                disabled={cancelling}
                variant="outline"
                size="sm"
                className="ml-2 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              >
                {cancelling ? <Loader2 size={14} className="animate-spin mr-1" /> : <Ban size={14} className="mr-1" />}
                {t("dr_job_cancel")}
              </Button>
            )}
          </div>
        </div>

        <div className="p-6">
          {/* Planner rationale */}
          {(progressData?.complexity || progressData?.planner_rationale) && (
            <div
              data-testid="dr-planner-info"
              className="rounded-xl border border-purple-500/30 bg-purple-500/5 p-3 mb-4 text-xs text-purple-200"
            >
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} className="text-purple-400" />
                <span className="font-semibold uppercase tracking-wide">
                  {t("dr_planner_decision")}: {t(`dr_complexity_${progressData.complexity || "medium"}`)}
                </span>
                <span className="ml-auto text-purple-300 font-mono">
                  {progressData?.total_agents || 0} {t("dr_agents_short")}
                </span>
              </div>
              {progressData?.planner_rationale && (
                <p className="text-zinc-400 leading-snug">{progressData.planner_rationale}</p>
              )}
            </div>
          )}

          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-zinc-300">
                {t("dr_progress")} ({progressData?.completed_agents || 0}/{progressData?.total_agents || 0} {t("dr_agents_short")})
              </span>
              <span className="text-sm font-semibold text-purple-300">{pct}%</span>
            </div>
            <Progress
              value={pct}
              className="h-2 bg-zinc-800"
              data-testid="dr-progress-bar"
            />
          </div>

          {/* Phases */}
          <div className="space-y-2 mb-6">
            {(progressData?.phases || []).map((phase, i) => (
              <PhaseRow key={i} phase={phase} t={t} />
            ))}
          </div>

          {/* Actions when complete */}
          {status === "completed" && (
            <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} className="text-green-400" />
                <span className="font-semibold text-green-300">{t("dr_report_ready")}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="dr-download-md"
                  onClick={() => downloadReport("md")}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-200"
                  disabled={downloading === "md"}
                >
                  {downloading === "md" ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileText size={14} className="mr-1" />}
                  Markdown
                </Button>
                <Button
                  data-testid="dr-download-pdf"
                  onClick={() => downloadReport("pdf")}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-200"
                  disabled={downloading === "pdf"}
                >
                  {downloading === "pdf" ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileType size={14} className="mr-1" />}
                  PDF
                </Button>
                <Button
                  data-testid="dr-download-docx"
                  onClick={() => downloadReport("docx")}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-200"
                  disabled={downloading === "docx"}
                >
                  {downloading === "docx" ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileText size={14} className="mr-1" />}
                  Word
                </Button>
                <Button
                  data-testid="dr-download-pptx"
                  onClick={() => downloadReport("pptx")}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-200"
                  disabled={downloading === "pptx"}
                >
                  {downloading === "pptx" ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileType size={14} className="mr-1" />}
                  PowerPoint
                </Button>
                <div className="text-[10px] text-zinc-500 self-center ml-1">
                  Google Docs: {t("dr_gdocs_note")}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-green-500/20">
                <Button
                  data-testid="dr-share-btn"
                  onClick={shareReport}
                  size="sm"
                  className="bg-sky-600 hover:bg-sky-500 text-white"
                  disabled={sharing}
                >
                  {sharing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Share2 size={14} className="mr-1" />}
                  {t("dr_share")}
                </Button>
              </div>
            </div>
          )}

          {status === "failed" && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={18} className="text-red-400" />
                <span className="font-semibold text-red-300">{t("dr_job_failed")}</span>
              </div>
              <p className="text-xs text-zinc-400 mb-3">{job?.error}</p>
              <Button
                data-testid="dr-resume-btn"
                onClick={resumeJob}
                size="sm"
                disabled={resuming}
                className="bg-green-600 hover:bg-green-500 text-white"
              >
                {resuming ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1" />}
                {t("dr_job_resume")}
              </Button>
            </div>
          )}

          {status === "cancelled" && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Ban size={18} className="text-amber-400" />
                <span className="font-semibold text-amber-300">{t("dr_cancelled")}</span>
              </div>
              <p className="text-xs text-zinc-400 mb-3">{t("dr_job_resume_hint")}</p>
              <Button
                data-testid="dr-resume-btn"
                onClick={resumeJob}
                size="sm"
                disabled={resuming}
                className="bg-green-600 hover:bg-green-500 text-white"
              >
                {resuming ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1" />}
                {t("dr_job_resume")}
              </Button>
            </div>
          )}

          {/* Report preview */}
          {status === "completed" && job?.report_md && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 max-h-[400px] overflow-y-auto">
              <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">
                {job.report_md.substring(0, 3000)}
                {job.report_md.length > 3000 && "\n\n[…]"}
              </pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StatusBadge = ({ status, t }) => {
  const map = {
    queued: { text: t("dr_queued"), color: "bg-zinc-600" },
    running: { text: t("dr_running"), color: "bg-purple-500" },
    completed: { text: t("dr_completed"), color: "bg-green-500" },
    failed: { text: t("dr_failed"), color: "bg-red-500" },
    cancelled: { text: t("dr_cancelled"), color: "bg-amber-500" },
  };
  const cfg = map[status] || map.queued;
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold text-white ${cfg.color}`}>
      {cfg.text}
    </span>
  );
};

const PhaseRow = ({ phase, t }) => {
  const isCompleted = phase.status === "completed";
  const isRunning = phase.status === "running";
  const Icon = isCompleted ? CheckCircle2 : isRunning ? Loader2 : Circle;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${isRunning ? "border-purple-500/40 bg-purple-500/5" : "border-zinc-800 bg-zinc-800/30"}`}>
      <Icon
        size={18}
        className={
          isCompleted
            ? "text-green-400"
            : isRunning
            ? "text-purple-400 animate-spin"
            : "text-zinc-600"
        }
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-200">{t(phase.label_key)}</div>
        <div className="text-xs text-zinc-500">
          {phase.agents_completed}/{phase.agents_total} {t("dr_agents_short")}
        </div>
      </div>
      {phase.agents_total > 0 && (
        <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
            style={{ width: `${Math.min(100, (phase.agents_completed * 100) / Math.max(1, phase.agents_total))}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default DeepResearchJobView;
